import {
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import { Dispatch, SetStateAction } from "react";
import { create } from "zustand";

import { auth } from "../../../../firebaseClient";
import { deleteUser } from "../../../api/user-management/deleteUser";
import { validateUser } from "../../../api/user-management/validateUser";
import { dialogMessage } from "../../../constants/dialogMessage";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../../../utils/common/clientOnlyLocalStorage";
import {
  BUTTONS,
  ErrorMessage,
  getDeleteAccountErrorMessage,
  getFirebaseAuthErrorMessage,
} from "../../../utils/common/getErrorMessage";
import { Locale } from "../../../utils/types";

const GF_WEB_IS_LOGGED_IN = "gfWeb_isLoggedIn";
const GF_WEB_LOGIN_METHOD = "gfWeb_loginMethod";
const GF_WEB_LOGIN_PHONE_NUMBER = "gfWeb_loginPhoneNumber";
const GF_WEB_LOGIN_EMAIL = "gfWeb_loginEmail";

type AuthLoginMethod = "email" | "phone" | "sns";

const toAuthLoginMethod = (value: string | null): AuthLoginMethod | null => {
  if (value === "email" || value === "phone" || value === "sns") {
    return value;
  }
  return null;
};

const persistLoginMethod = (method: AuthLoginMethod | null) => {
  if (method) {
    setLocalStorageItem(GF_WEB_LOGIN_METHOD, method);
  } else {
    removeLocalStorageItem(GF_WEB_LOGIN_METHOD);
  }
};

const persistLoginPhoneNumber = (phoneNumber: string | null) => {
  if (phoneNumber) {
    setLocalStorageItem(GF_WEB_LOGIN_PHONE_NUMBER, phoneNumber);
  } else {
    removeLocalStorageItem(GF_WEB_LOGIN_PHONE_NUMBER);
  }
};

const persistLoginEmail = (email: string | null) => {
  if (email) {
    setLocalStorageItem(GF_WEB_LOGIN_EMAIL, email);
  } else {
    removeLocalStorageItem(GF_WEB_LOGIN_EMAIL);
  }
};

const isPhoneFormatEmail = (email: string | null): boolean => {
  return /^010-\d{4}-\d{4}@moais\.co\.kr$/.test(email || "");
};

export const checkLoggedIn = () => {
  return getLocalStorageItem(GF_WEB_IS_LOGGED_IN) === "true";
};

// 쿠키에 로그인 플래그 설정 및 제거
export const setLoginFlag = () => {
  setLocalStorageItem(GF_WEB_IS_LOGGED_IN, "true");
};

export const clearLoginFlag = () => {
  removeLocalStorageItem(GF_WEB_IS_LOGGED_IN);
  removeLocalStorageItem(GF_WEB_LOGIN_METHOD);
  removeLocalStorageItem(GF_WEB_LOGIN_PHONE_NUMBER);
  removeLocalStorageItem(GF_WEB_LOGIN_EMAIL);
};

interface AuthState {
  currentUser: User | null;
  token: string | null;
  loginMethod: AuthLoginMethod | null;
  phoneNumber: string | null;
  email: string | null;
  isLoading: boolean | null;
  // authError: ErrorMessage | null;
  redirected: boolean | null; // 리디렉션 플래그
  // 회원탈퇴 다이얼로그 표시를 위한 상태 추가, 일괄 관리 예정
  showDeleteAccountDialog: boolean;
  isDeleted: boolean;
}

interface AuthActions {
  // 구글 로그인
  googleSignIn: (
    locale: Locale
  ) => Promise<
    | { success: true; error?: undefined }
    | { success: false; error?: undefined }
    | { success: false; error: ErrorMessage }
  >;

  // 애플 로그인
  appleSignIn: (
    locale: Locale
  ) => Promise<
    | { success: true; error?: undefined }
    | { success: false; error?: undefined }
    | { success: false; error: ErrorMessage }
  >;

  // 전화번호 로그인
  phoneSignIn: (
    phoneNumber: string,
    password: string,
    locale: Locale,
    reAuth?: boolean
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { success: boolean; error: ErrorMessage }
  >;

  // 이메일 로그인
  emailSignIn: (
    email: string,
    password: string,
    locale: Locale,
    reAuth?: boolean
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { success: boolean; error: ErrorMessage }
  >;

  // 비밀번호 재설정 이메일 발송
  sendResetPasswordEmail: (
    email: string,
    locale: Locale
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { success: boolean; error: ErrorMessage }
  >;

  // 로그아웃
  logOut: (
    locale: Locale
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { success: boolean; error: ErrorMessage }
  >;

  // 회원탈퇴
  deleteUserAccount: (
    locale: Locale
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { success: boolean; error: ErrorMessage }
  >;

  // 유저 정보 및 토큰 설정
  setUser: (user: User | null, token: string | null) => void;

  // 전화번호 로그인 유저의 전화번호 설정
  setPhoneNumber: (phoneNumber: string | null) => void;

  // 인증 상태 초기화 및 로딩 상태 설정
  initializeAuthState: (
    isInitialLoading: boolean,
    setIsInitialLoading: Dispatch<SetStateAction<boolean>>
  ) => void;

  // 로딩 상태 설정
  setIsLoading: (isLoading: AuthState["isLoading"]) => void;

  // 회원탈퇴 다이얼로그 제어
  setShowDeleteAccountDialog: (show: boolean) => void;

  setIsDeleted: (isDeleted: boolean) => void;
}

const useUserAuthStore = create<AuthState & AuthActions>(set => ({
  currentUser: null,
  token: null,
  // 로그인 방법
  loginMethod: null,
  // 전화번호 로그인 유저의 회원탈퇴 시 비밀번호 확인 페이지에서 전화번호를 전달하기 위한 변수
  phoneNumber: null,
  // 이메일 로그인 유저의 이메일 저장
  email: null,
  isLoading: null,
  // authError: null,
  redirected: null,
  showDeleteAccountDialog: false,
  isDeleted: false,

  setIsDeleted: isDeleted => set({ isDeleted }),

  googleSignIn: async (locale: Locale) => {
    set({
      isLoading: true,
    });

    const googleAuthProvider = new GoogleAuthProvider();
    try {
      const userCred = await signInWithPopup(auth, googleAuthProvider);
      const { user } = userCred;
      const token = await user.getIdToken();

      const { validationResult } = await validateUser({
        userId: user.uid,
        token,
      });

      const isValidUser =
        validationResult?.status === "OK" &&
        validationResult?.availability === "AVAILABLE";

      if (isValidUser) {
        set({
          currentUser: user,
          token,
          loginMethod: "sns",
          phoneNumber: null,
          email: null,
          isLoading: false,
        });
        persistLoginMethod("sns");
        persistLoginPhoneNumber(null);
        persistLoginEmail(null);
        setLoginFlag();
        return { success: true };
      }

      set({
        currentUser: null,
        token: null,
        loginMethod: null,
        phoneNumber: null,
        email: null,
        isLoading: false,
      });

      return {
        success: false,
        error: {
          title:
            dialogMessage.loginFailed.firebaseAuthUnregisteredOrWrongCredentials
              .title,
          message:
            dialogMessage.loginFailed.firebaseAuthUnregisteredOrWrongCredentials
              .message,
          buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
        },
      };
    } catch (err) {
      const errorMessage = getFirebaseAuthErrorMessage(err, locale);

      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  appleSignIn: async (locale: Locale) => {
    set({
      isLoading: true,
    });

    const appleAuthProvider = new OAuthProvider("apple.com");
    try {
      const userCred = await signInWithPopup(auth, appleAuthProvider);
      const { user } = userCred;
      const token = await user.getIdToken();

      const { validationResult } = await validateUser({
        userId: user.uid,
        token,
      });

      const isValidUser =
        validationResult?.status === "OK" &&
        validationResult?.availability === "AVAILABLE";

      if (isValidUser) {
        set({
          currentUser: user,
          token,
          loginMethod: "sns",
          phoneNumber: null,
          email: null,
          isLoading: false,
        });
        persistLoginMethod("sns");
        persistLoginPhoneNumber(null);
        persistLoginEmail(null);
        setLoginFlag();
        return { success: true };
      }

      await signOut(auth);
      set({
        currentUser: null,
        token: null,
        loginMethod: null,
        phoneNumber: null,
        email: null,
        isLoading: false,
      });

      return {
        success: false,
        error: {
          title:
            dialogMessage.loginFailed.firebaseAuthUnregisteredOrWrongCredentials
              .title,
          message:
            dialogMessage.loginFailed.firebaseAuthUnregisteredOrWrongCredentials
              .message,
          buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
        },
      };
    } catch (err) {
      const errorMessage = getFirebaseAuthErrorMessage(err, locale);

      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false }); // 끝날 때 로딩 상태 해제
    }
  },

  phoneSignIn: async (
    phoneNumber: string,
    password: string,
    locale: Locale,
    // 비밀번호 재확인
    reAuth?: boolean
  ) => {
    set({
      isLoading: true,
    });

    try {
      const email = `${phoneNumber}@moais.co.kr`; // 전화번호를 이메일 형식으로 변환

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const { user } = userCredential;

      const token = await user.getIdToken();

      // 이메일 인증이 되지 않은 경우 전화번호 유저
      if (!user.emailVerified) {
        set({ phoneNumber });
      }

      set({
        currentUser: user,
        token,
        loginMethod: "phone",
        phoneNumber,
        email: null,
      });
      persistLoginMethod("phone");
      persistLoginPhoneNumber(phoneNumber);
      persistLoginEmail(null);
      setLoginFlag(); // 로그인 플래그 설정

      // 비밀번호 재확인인 경우 회원 탈퇴 다이얼로그를 보여줌
      if (reAuth) {
        set({ showDeleteAccountDialog: true });
      }

      return { success: true };
    } catch (err) {
      const errorMessage = getFirebaseAuthErrorMessage(err, locale, reAuth);
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  emailSignIn: async (
    email: string,
    password: string,
    locale: Locale,
    reAuth?: boolean
  ) => {
    set({
      isLoading: true,
    });

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const { user } = userCredential;
      const token = await user.getIdToken();

      set({
        currentUser: user,
        token,
        loginMethod: "email",
        phoneNumber: null,
        email,
      });
      persistLoginMethod("email");
      persistLoginPhoneNumber(null);
      persistLoginEmail(email);
      setLoginFlag();

      // 비밀번호 재확인인 경우 회원 탈퇴 다이얼로그를 보여줌
      if (reAuth) {
        set({ showDeleteAccountDialog: true });
      }

      return { success: true };
    } catch (err) {
      const errorMessage = getFirebaseAuthErrorMessage(err, locale);
      return { success: false, error: { ...errorMessage, title: "" } };
    } finally {
      set({ isLoading: false });
    }
  },

  sendResetPasswordEmail: async (email: string, locale: Locale) => {
    set({ isLoading: true });
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      const errorMessage = getFirebaseAuthErrorMessage(err, locale);
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  logOut: async (locale: Locale) => {
    set({
      isLoading: true,
    });

    try {
      await signOut(auth);
      clearLoginFlag();
      // !: page refresh
      window.location.reload();

      set({
        token: null,
        currentUser: null,
        loginMethod: null,
        phoneNumber: null,
        email: null,
        isLoading: false,
      });

      return { success: true };
    } catch {
      return {
        success: false,
        error: {
          title: dialogMessage.logoutFailed.title,
          message: dialogMessage.logoutFailed.message,
          buttons: BUTTONS(locale).LOGOUT_ERROR_CONFIRM,
        },
      };
    } finally {
      set({ isLoading: false });
    }
  },

  deleteUserAccount: async (locale: Locale) => {
    set({
      isLoading: true,
    });

    // 유저 아이디와 토큰을 가져옴
    const { token } = useUserAuthStore.getState();

    try {
      const { error } = await deleteUser({
        token: token ?? "",
      });

      if (error) {
        set({
          currentUser: null,
          token: null,
          loginMethod: null,
          phoneNumber: null,
          email: null,
          isLoading: false,
        });

        return {
          success: false,
          error: {
            title:
              dialogMessage.loginFailed
                .firebaseAuthUnregisteredOrWrongCredentials.title,
            message:
              dialogMessage.loginFailed
                .firebaseAuthUnregisteredOrWrongCredentials.message,
            buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
          },
        };
      }
      set({ isDeleted: true });

      set({
        currentUser: null,
        token: null,
        loginMethod: null,
        phoneNumber: null,
        email: null,
        showDeleteAccountDialog: false,
      });

      return { success: true };
    } catch (err) {
      const errorMessage = getDeleteAccountErrorMessage(err, locale);

      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  setUser: (user, token) => set({ currentUser: user, token }),
  setPhoneNumber: phoneNumber => set({ phoneNumber }),
  setIsLoading: isLoading => set({ isLoading }),
  setShowDeleteAccountDialog: show => set({ showDeleteAccountDialog: show }),
  // setAuthError: error => set({ authError: error }),

  initializeAuthState: (
    isInitialLoading: boolean,
    setIsInitialLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    // eslint-disable-next-line sonarjs/cognitive-complexity -- 인증 상태별 초기화 분기(32/15). 로그인 플로우 핵심 경로라 동작 보존을 위해 분해하지 않음.
    onAuthStateChanged(auth, async user => {
      const isLoggedIn = checkLoggedIn();
      // !: 로그인 후 로딩 상태를 해제하기 위해
      if (!isLoggedIn) {
        // 세션에서 로그인 플래그가 없다면, 로딩 상태를 바로 해제
        // 로그인 완료 후 로그인 페이지에서 로딩 상태를 해제하기 위해
        set({
          isLoading: false,
        });
        return;
      }

      // !: 로그인 상태 확인
      if (user) {
        // isInitialLoading가 false인 경우 실행 안함
        if (!isInitialLoading) {
          return;
        }

        const token = await user.getIdToken();
        // !: 회원 유효성 검증
        const {
          validationResult,
          // error
        } = await validateUser({
          userId: user.uid,
          token,
        });

        const isValidUser =
          validationResult?.status === "OK" &&
          validationResult?.availability === "AVAILABLE";

        if (isValidUser) {
          // !: 로그인 방법 확인
          const storedLoginMethod = toAuthLoginMethod(
            getLocalStorageItem(GF_WEB_LOGIN_METHOD)
          );
          // !: 전화번호 확인
          const storedPhoneNumber = getLocalStorageItem(
            GF_WEB_LOGIN_PHONE_NUMBER
          );
          // !: 이메일 확인
          const storedLoginEmail = getLocalStorageItem(GF_WEB_LOGIN_EMAIL);

          let resolvedLoginMethod: AuthLoginMethod | null = storedLoginMethod;
          let resolvedPhoneNumber: string | null = storedPhoneNumber;
          let resolvedEmail: string | null = storedLoginEmail;

          if (!resolvedLoginMethod) {
            const providerId = user.providerData?.[0]?.providerId;
            const userEmail = user.email || null;
            const userHandle = userEmail?.split("@")[0] ?? null;

            if (providerId === "google.com" || providerId === "apple.com") {
              resolvedLoginMethod = "sns";
            } else if (providerId === "password") {
              const isPhoneEmail = isPhoneFormatEmail(userEmail);
              resolvedLoginMethod = isPhoneEmail ? "phone" : "email";
              resolvedPhoneNumber = isPhoneEmail
                ? (resolvedPhoneNumber ?? userHandle)
                : resolvedPhoneNumber;
              resolvedEmail = isPhoneEmail
                ? null
                : (resolvedEmail ?? userEmail);
            } else {
              resolvedLoginMethod = "email";
              resolvedPhoneNumber = resolvedPhoneNumber ?? userHandle;
            }
          }

          set({
            token,
            currentUser: user,
            loginMethod: resolvedLoginMethod,
            phoneNumber: resolvedPhoneNumber,
            email: resolvedEmail,
            isLoading: false,
          });
          setLoginFlag();
          setIsInitialLoading(false);
          return;
        }
        // 리디렉션 플래그 확인 - 로그인 시도 후 발생한 에러인 경우에는 리디렉션 플래그가 있을 수 있음
        await signOut(auth);
        clearLoginFlag(); // 로그아웃 시 세션에서 로그인 플래그 제거
        set({
          currentUser: null,
          token: null,
          loginMethod: null,
          phoneNumber: null,
          email: null,
          isLoading: false,
        });
      } else {
        set({
          currentUser: null,
          token: null,
          loginMethod: null,
          phoneNumber: null,
          email: null,
          isLoading: false,
        });
      }

      setIsInitialLoading(false);
    });
  },
}));

export default useUserAuthStore;
